/**
 * A frame's text, as somebody reads and rewrites it.
 *
 * Printed, a frame is lines of positioned words. Rewritten, it is runs of
 * text that flow. Turning the first into the second joins the lines back into
 * sentences, undoing the hyphen a justified line ended on, and merges
 * neighbouring words set alike, so a paragraph with one bold phrase is three
 * runs rather than ninety.
 *
 * Pure.
 */

import type { EditedRun, FacsimileFrame, FacsimileRun } from '@/domain/facsimile/types'

/**
 * What a run shows.
 *
 * A glyph the PDF gave no character for arrives as U+FFFD. Alone, it is
 * almost always a list bullet drawn from a symbol font, and is shown as one.
 */
export function shownText(text: string): string {
  return text === '�' ? '•' : text
}

/** A piece of a word drawn from one position: the word's start, or an anchored letter. */
export interface Segment {
  readonly x: number
  readonly text: string
  /** A ligature drawn apart, which must be set without letter spacing to form. */
  readonly ligature: boolean
}

/**
 * A word cut where its anchors fall, each piece with the x it starts at.
 *
 * Cut by characters, so a ligature's letters always stay in one piece and
 * are still drawn as the ligature.
 */
export function segmentsOf(run: FacsimileRun): Segment[] {
  const characters = [...shownText(run.text)]
  const starts = [
    [0, 0] as const,
    ...run.anchors.filter(([index]) => index > 0 && index < characters.length),
  ]
  return starts.map(([index, offset], at) => ({
    x: run.x + offset,
    text: characters.slice(index, starts[at + 1]?.[0] ?? characters.length).join(''),
    ligature: run.ligatures.includes(index),
  }))
}

function sameStyle(a: EditedRun, b: Pick<EditedRun, 'font' | 'size' | 'colour'>): boolean {
  return a.font === b.font && a.size === b.size && a.colour === b.colour
}

/** Runs with neighbours set alike merged, and empty ones dropped. */
export function normaliseRuns(runs: readonly EditedRun[]): EditedRun[] {
  const merged: EditedRun[] = []
  for (const run of runs) {
    if (run.text === '') continue
    const last = merged.at(-1)
    if (last && sameStyle(last, run))
      merged[merged.length - 1] = { ...last, text: last.text + run.text }
    else merged.push({ ...run })
  }
  return merged
}

/** Whether a line break falls inside a word the justification hyphenated. */
function hyphenated(previous: string, next: string): boolean {
  return /[a-zà-ÿ]-$/i.test(previous) && /^[a-zà-ÿ]/.test(next)
}

function asEdited(run: FacsimileRun, text: string): EditedRun {
  return { text, font: run.font, size: run.size, colour: run.colour }
}

/** A gap between two words wider than this share of the type size is a space. */
const WORD_GAP = 0.12

/** Whether the PDF left a space between two runs of one line: a comma after a bold word has none. */
function spaced(before: FacsimileRun, after: FacsimileRun): boolean {
  return after.x - (before.x + before.width) > Math.min(before.size, after.size) * WORD_GAP
}

/** What goes between a run and what came before it: a space, nothing, or the end of a hyphen. */
function joint(
  line: readonly FacsimileRun[],
  index: number,
  previous: string,
  text: string,
): string | null {
  const before = line[index - 1]
  if (before) return spaced(before, line[index] ?? before) ? ' ' : ''
  if (previous === '') return ''
  // A new line: a space, unless the line before split this word with a hyphen.
  return hyphenated(previous, text) ? null : ' '
}

/** The frame's printed text, as runs that flow. */
export function printedRuns(frame: FacsimileFrame): EditedRun[] {
  const runs: EditedRun[] = []
  let previous = ''
  for (const line of frame.lines) {
    line.runs.forEach((run, index) => {
      const text = shownText(run.text)
      const between = joint(line.runs, index, previous, text)
      if (between === null) {
        // Take the hyphen back off the word it split.
        const last = runs[runs.length - 1]
        if (last) runs[runs.length - 1] = { ...last, text: last.text.slice(0, -1) }
      }
      runs.push(asEdited(run, (between ?? '') + text))
      previous = text
    })
  }
  return normaliseRuns(runs)
}

/** What the frame says now: the rewrite, or the print. */
export function currentRuns(frame: FacsimileFrame): EditedRun[] {
  return frame.edited ? [...frame.edited] : printedRuns(frame)
}

export function plainText(runs: readonly EditedRun[]): string {
  return runs.map((run) => run.text).join('')
}

/**
 * The frame rewritten.
 *
 * A rewrite that says exactly what was printed is no rewrite: the frame goes
 * back to being drawn word for word, so editing and undoing the edit by hand
 * leaves the page as exact as it was.
 */
export function rewrite(frame: FacsimileFrame, runs: readonly EditedRun[]): FacsimileFrame {
  const next = normaliseRuns(runs)
  const printed = printedRuns(frame)
  const unchanged =
    next.length === printed.length &&
    next.every((run, index) => {
      const other = printed[index]
      return other !== undefined && run.text === other.text && sameStyle(run, other)
    })
  return { ...frame, edited: unchanged ? null : next }
}

export function restore(frame: FacsimileFrame): FacsimileFrame {
  return { ...frame, edited: null }
}

/** The style most of the frame's text is set in, for a rewrite that loses its runs. */
export function dominantStyle(frame: FacsimileFrame): Pick<EditedRun, 'font' | 'size' | 'colour'> {
  const weights = new Map<
    string,
    { style: Pick<EditedRun, 'font' | 'size' | 'colour'>; count: number }
  >()
  for (const run of currentRuns(frame)) {
    const key = `${run.font}|${run.size}|${run.colour}`
    const held = weights.get(key) ?? { style: run, count: 0 }
    held.count += run.text.length
    weights.set(key, held)
  }
  const best = [...weights.values()].sort((a, b) => b.count - a.count)[0]
  return best ? best.style : { font: '', size: 10, colour: '#000000' }
}

/**
 * The distance between lines, as a multiple of the type size.
 *
 * Read off the printed baselines, so a rewritten paragraph keeps the leading
 * the designer gave it. 1.2 when there is a single line to go by.
 */
export function leading(frame: FacsimileFrame): number {
  const gaps = frame.lines
    .slice(1)
    .map((line, index) => line.baseline - (frame.lines[index]?.baseline ?? line.baseline))
    .filter((gap) => gap > 0)
    .sort((a, b) => a - b)
  const size = dominantStyle(frame).size
  const middle = gaps[Math.floor(gaps.length / 2)]
  return middle && size > 0 ? Math.round((middle / size) * 100) / 100 : 1.2
}

/** The SVG transform that turns a frame about its middle, or undefined for an upright one. */
export function frameTurn(frame: FacsimileFrame): string | undefined {
  if (!frame.rotation) return undefined
  const [left, top, right, bottom] = frame.box
  return `rotate(${frame.rotation} ${(left + right) / 2} ${(top + bottom) / 2})`
}
